import React, { useEffect, useState } from "react";
import { X } from "lucide-react";

/**
 * Modal that prompts for resolution / fulfilment / implementation notes
 * before applying a status transition. Notes are mandatory and CAN'T be
 * edited from anywhere else in the UI.
 *
 * Props:
 *   open: boolean
 *   onClose(): close without action
 *   onSubmit(notes: string): called on confirm; caller performs the PUT
 *   title: dialog title (e.g. "Resolve incident", "Close service request")
 *   fieldLabel: label above the textarea (e.g. "Resolution notes", "Fulfilment notes")
 *   actionLabel: button text (e.g. "Resolve & Close")
 *   tone: "primary" | "danger" — affects button colour
 *   required: bool — if true, empty notes are blocked
 */
export default function ResolutionDialog({
  open, onClose, onSubmit,
  title = "Add resolution notes",
  fieldLabel = "Resolution notes",
  actionLabel = "Submit",
  tone = "primary",
  required = true,
}) {
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { if (open) setText(""); }, [open]);

  if (!open) return null;

  async function submit() {
    if (required && !text.trim()) {
      alert("Notes are required for this action.");
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit(text.trim());
    } finally {
      setSubmitting(false);
    }
  }

  const btn = tone === "danger"
    ? "bg-gradient-to-r from-rose-500 to-red-600 hover:from-rose-600 hover:to-red-700 text-white"
    : "bg-gradient-to-r from-brand-500 to-brand-600 hover:from-brand-600 hover:to-brand-700 text-white";

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fade-in" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-elevated w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 bg-gradient-to-r from-brand-500 to-brand-600 text-white rounded-t-xl">
          <span className="font-semibold">{title}</span>
          <button onClick={onClose} className="p-1 hover:bg-white/20 rounded transition" aria-label="Close">
            <X size={16} />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <label className="label">{fieldLabel} {required && <span className="text-rose-600">*</span>}</label>
          <textarea
            className="input"
            rows="6"
            autoFocus
            placeholder="What was done, root cause, follow-up actions, comms sent..."
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          <p className="text-xs text-slate-500">
            These notes are captured as a permanent audit entry in the resolution history. They cannot be edited later.
          </p>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 bg-slate-50 rounded-b-xl">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={submitting}>Cancel</button>
          <button type="button" className={`px-4 py-2 rounded-lg font-semibold shadow-soft transition ${btn} disabled:opacity-60`} onClick={submit} disabled={submitting}>
            {submitting ? "Saving…" : actionLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
